// StreakOverview.js
// Pre-game how-to screen for Streak Mode. Mirrors StreakOverviewView.swift:
//   "{SPORT} STREAKS" header → how-to card → best/games card → BEGIN STREAKS.

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import wordmark from "../assets/Rosterable.png";
import lightningIcon from "../assets/lightning-fill.svg";
import { useAuth } from "../managers/AuthContext";
import { getStreakStats } from "../managers/streakService";
import { getStreakAllTimeBest } from "../managers/leaderboardService";

const HOW_TO = [
  { icon: "⬆️", text: "Pick the player with the highest rating" },
  { icon: "✅", text: "Each correct pick extends your streak" },
  { icon: "❌", text: "3 incorrect picks ends the game" },
  { icon: "⏱️", text: "10 seconds per pick" },
  { icon: "⚡", text: "1 Rating Reveal & 1 Player Refresh per game" },
];

// Maximum gap between the last content card and the BEGIN button. The spacer
// below grows up to this distance, then stops — so the button never drifts to
// the bottom of a tall window. Tune this one value to move the button up/down.
const CTA_MAX_GAP = "32px";

export default function StreakOverview() {
  const navigate = useNavigate();
  const location = useLocation();
  const sport = location.state?.sport;

  useEffect(() => { if (!sport) navigate("/"); }, [sport, navigate]);
  if (!sport) return null;

  return <StreakOverviewInner sport={sport} navigate={navigate} />;
}

function StreakOverviewInner({ sport, navigate }) {
  const { user } = useAuth();
  const local = getStreakStats(user?.uid || "guest", sport.id);

  // BEST STREAK: signed-in users pull their all-time best from the leaderboard
  // data (cross-device); seed with the local value so it shows instantly offline.
  // GAMES PLAYED stays a local counter — it isn't leaderboard data.
  const [bestStreak, setBestStreak] = useState(local.allTimeBest);
  useEffect(() => {
    if (!user) return;
    getStreakAllTimeBest(user.uid, sport.id)
      .then(best => setBestStreak(prev => Math.max(best, prev)))
      .catch(() => {});
  }, [user, sport.id]);

  return (
    <div style={styles.container}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate("/")}>‹</button>
        <button style={styles.wordmarkBtn} onClick={() => navigate("/")} aria-label="Home">
          <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
        </button>
        <div style={{ width: 44 }} />
      </div>

      {/* ── Title + gradient rule ── */}
      <div style={styles.titleRow}>
        <span style={{ ...styles.title, color: sport.hex }}>
          {sport.name.toUpperCase()} STREAKS
        </span>
        <div style={{ ...styles.titleRule, background: `linear-gradient(to right, ${sport.hex}8c, transparent)` }} />
      </div>

      {/* ── How to play ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <img src={lightningIcon} alt="" style={{ ...styles.cardHeaderIcon, filter: "brightness(0) invert(1)", opacity: 0.6 }} />
          <span style={styles.cardHeaderText}>HOW TO PLAY</span>
        </div>
        {HOW_TO.map((row, i) => (
          <div key={i} style={styles.howRow}>
            <span style={styles.howIcon}>{row.icon}</span>
            <span style={styles.howText}>{row.text}</span>
          </div>
        ))}
      </div>

      {/* ── Best / games ── */}
      <div style={{ ...styles.statCard, borderColor: `${sport.hex}38` }}>
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue, color: sport.hex }}>
            {bestStreak > 0 ? bestStreak : "—"}
          </span>
          <span style={styles.statLabel}>BEST STREAK</span>
        </div>
        <div style={styles.statDivider} />
        <div style={styles.statItem}>
          <span style={{ ...styles.statValue, color: sport.hex }}>
            {local.gamesPlayed > 0 ? local.gamesPlayed : "—"}
          </span>
          <span style={styles.statLabel}>GAMES PLAYED</span>
        </div>
      </div>

      {/* Capped spacer: grows to at most CTA_MAX_GAP below the content, so the
          button sits a fixed distance below the cards on tall windows (never
          tracking the bottom edge). It can still shrink on short windows, which
          lets the button move UP to stay on screen — but never further down. */}
      <div style={{ flex: 1, maxHeight: CTA_MAX_GAP }} />

      {/* ── Begin ── */}
      <BeginButton sport={sport} onClick={() => navigate("/streak", { state: { sport } })} />
    </div>
  );
}

function BeginButton({ sport, onClick }) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        ...styles.beginBtn,
        background: `linear-gradient(to right, ${sport.hex}, ${sport.hex}cc)`,
        boxShadow: `0 8px 22px ${sport.hex}66`,
        transform: pressed ? "scale(0.98)" : "scale(1)",
        transition: "transform 0.1s ease",
      }}
    >
      <img src={lightningIcon} alt="" style={styles.beginIcon} />
      <span style={styles.beginLabel}>BEGIN STREAKS</span>
      <span style={{ width: 18 }} />
    </button>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 16px 24px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "52px",
    paddingBottom: "16px",
  },
  backBtn: {
    background: "none", border: "none", color: "rgba(255,255,255,0.8)",
    fontSize: "28px", fontWeight: "300", lineHeight: 1, padding: "4px 8px 4px 0", width: "44px", cursor: "pointer",
  },
  wordmark: { height: "21px", width: "auto" },
  wordmarkBtn: {
    background: "none", border: "none", padding: 0, cursor: "pointer",
    display: "flex", alignItems: "center",
  },

  titleRow: { display: "flex", alignItems: "center", gap: "10px", paddingTop: "6px", paddingBottom: "16px" },
  title: { fontSize: "19px", fontWeight: "500", letterSpacing: "2.5px", whiteSpace: "nowrap" },
  titleRule: { flex: 1, height: "1px" },

  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "10px",
  },
  cardHeader: { display: "flex", alignItems: "center", gap: "6px" },
  cardHeaderIcon: { width: "10px", height: "10px" },
  cardHeaderText: { fontSize: "10px", fontWeight: "800", color: "rgba(255,255,255,0.5)", letterSpacing: "1.2px" },
  howRow: { display: "flex", alignItems: "center", gap: "10px" },
  howIcon: { fontSize: "14px", width: "20px", textAlign: "center", flexShrink: 0 },
  howText: { fontSize: "13px", color: "rgba(255,255,255,0.75)" },

  statCard: {
    display: "flex",
    alignItems: "center",
    borderRadius: "16px",
    border: "1px solid",
    background: "rgba(255,255,255,0.02)",
    padding: "12px 0",
  },
  statItem: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" },
  statValue: { fontSize: "32px", fontWeight: "800", letterSpacing: "0.5px" },
  statLabel: { fontSize: "11px", fontWeight: "600", color: "rgba(255,255,255,0.45)", letterSpacing: "1px" },
  statDivider: { width: "1px", height: "36px", background: "rgba(255,255,255,0.08)" },

  beginBtn: {
    width: "100%",
    border: "none",
    borderRadius: "16px",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
  },
  beginIcon: { width: "18px", height: "18px", filter: "brightness(0) invert(1)" },
  beginLabel: { fontSize: "18px", fontWeight: "800", color: "#fff", letterSpacing: "1px" },
};
